/**
 * @file     contextBuilder.ts
 * @purpose  Context management for the chat engine: a structured block
 *           (deterministic summary of what the buyer has established + filter
 *           state + live count) plus only the last few verbatim turns, instead
 *           of replaying the raw transcript. No window-falls-off amnesia, and
 *           each call ships a bounded context.
 */

import { filterChips } from '@/services/mls/filterChips';
import type { AiFilters } from '@/services/mls/aiFilterSchema';
import type { ChatTurnMessage } from './types';
import { promptCount } from './countRule';

/** Verbatim tail: two full exchanges. Everything older is summarized. */
export const VERBATIM_TURNS = 4;
const MAX_NOTES = 6;
const NOTE_MAX_CHARS = 160;
const MESSAGE_MAX_CHARS = 1000;

/** A buyer statement worth carrying forward even though it changed no filter
 *  (preferences, constraints, life context). Deterministic: any short user
 *  message from a non-search turn qualifies. */
export function extractNote(userMessage: string, action: string): string | null {
  if (action !== 'answer' && action !== 'ask' && action !== 'commute_handoff') return null;
  const trimmed = userMessage.trim();
  if (!trimmed || trimmed.length > NOTE_MAX_CHARS) return null;
  return trimmed;
}

export function appendNote(notes: string[], note: string | null): string[] {
  if (!note) return notes;
  if (notes.includes(note)) return notes;
  return [...notes, note].slice(-MAX_NOTES);
}

export interface ContextInput {
  filters: AiFilters;
  count: number | null;
  notes: string[];
  totalTurns: number;
  /** True when filters were seeded from the buyer's saved profile and nothing
   *  has been said in THIS conversation yet: remembered preferences, not a
   *  stated search. */
  seeded?: boolean;
  /** What the client actually rendered last turn (e.g. every match already
   *  shown), so the model never argues with the buyer's screen. */
  clientNote?: string;
  /** Server-built description of the buyer's manual filter edits this turn +
   *  recomputed before/after counts, so the model can react to a harmful one. */
  manualEdits?: string;
  /** Server-selected context for an allowlisted first-party SEO surface. Never
   *  accept free-form client text here. */
  pageContext?: string;
}

/** The structured block appended to the system prompt each turn. */
export function buildContextBlock({
  filters,
  count,
  notes,
  totalTurns,
  seeded,
  clientNote,
  manualEdits,
  pageContext,
}: ContextInput): string {
  const chips = filterChips(filters);
  const lines = [
    'Conversation context (established so far):',
    chips.length === 0
      ? '- No search criteria established yet.'
      : seeded
        ? `- Saved budget/household preferences from the buyer's earlier visits (NOT stated in this conversation): ${chips.join(' · ')}. Apply them only where this conversation is silent, and NEVER narrate changes relative to them; the buyer's message here is the source of truth.`
        : `- Search so far: ${chips.join(' · ')}`,
    `- Live result count for that search: ${promptCount(count)}`,
  ];
  if (notes.length > 0) {
    lines.push(`- Buyer has also said: ${notes.map((n) => `"${n}"`).join(' | ')}`);
  }
  if (clientNote) {
    lines.push(`- Display note from the site: ${clientNote}`);
  }
  if (manualEdits) {
    lines.push(`- ${manualEdits}`);
  }
  if (pageContext) {
    lines.push(`- Trusted page context: ${pageContext}`);
  }
  if (totalTurns > VERBATIM_TURNS) {
    lines.push(`- Earlier turns are summarized above; only the last ${VERBATIM_TURNS} messages are verbatim.`);
  }
  return lines.join('\n');
}

/** The verbatim tail sent as real messages, re-anchored to open on a user
 *  turn (the Messages API requires it). */
export function verbatimTail(messages: ChatTurnMessage[]): Array<{ role: 'user' | 'assistant'; content: string }> {
  let tail = messages
    .filter((m) => !m.error)
    .slice(-VERBATIM_TURNS)
    .map((m) => ({ role: m.role, content: m.content.slice(0, MESSAGE_MAX_CHARS) }));
  const firstUser = tail.findIndex((m) => m.role === 'user');
  if (firstUser === -1) return [];
  if (firstUser > 0) tail = tail.slice(firstUser);
  return tail;
}
